import { Bot, Cpu, Link2 } from 'lucide-react';
import { Badge, Select, Spinner } from '@/components/ui';
import type { Agent, Model } from './types';

function modelLabel(model: Model) {
  return model.display_name || (model.name.includes('/') ? model.name.split('/').pop() : model.name);
}

/** Agent + model selectors shown in the chat header. */
export function AgentModelPicker({
  agents,
  models,
  selectedAgent,
  selectedModel,
  onAgentChange,
  onModelChange,
  loadingAgents,
  loadingModels,
  disabled,
  sessionActive,
}: {
  agents: Agent[];
  models: Model[];
  selectedAgent: string;
  selectedModel: string;
  onAgentChange: (path: string) => void;
  onModelChange: (name: string) => void;
  loadingAgents: boolean;
  loadingModels: boolean;
  disabled: boolean;
  sessionActive: boolean;
}) {
  const selectedAgentData = agents.find((a) => a.path === selectedAgent);
  const selectedModelData = models.find((m) => m.name === selectedModel);

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-0 flex-1 basis-56">
        <label htmlFor="chat-agent" className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Bot className="h-3.5 w-3.5" /> Agent
        </label>
        {loadingAgents ? (
          <div className="flex h-9 items-center rounded-md border border-input bg-card px-3">
            <Spinner label="Agents laden…" />
          </div>
        ) : (
          <Select
            id="chat-agent"
            value={selectedAgent}
            onChange={(e) => onAgentChange(e.target.value)}
            disabled={disabled}
            className="h-9"
            title={selectedAgentData?.path}
          >
            {agents.length === 0 && <option value="">Geen agents beschikbaar</option>}
            {agents.map((agent) => (
              <option key={agent.path} value={agent.path}>
                {agent.display_name}
              </option>
            ))}
          </Select>
        )}
      </div>

      <div className="min-w-0 flex-1 basis-56">
        <label htmlFor="chat-model" className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Cpu className="h-3.5 w-3.5" /> Model
        </label>
        {loadingModels ? (
          <div className="flex h-9 items-center rounded-md border border-input bg-card px-3">
            <Spinner label="Modellen laden…" />
          </div>
        ) : (
          <Select
            id="chat-model"
            value={selectedModel}
            onChange={(e) => onModelChange(e.target.value)}
            disabled={disabled}
            className="h-9"
            title={selectedModelData?.description}
          >
            {models.length === 0 && <option value="">Geen modellen beschikbaar</option>}
            {models.map((model) => (
              <option key={model.name} value={model.name}>
                {modelLabel(model)}
              </option>
            ))}
          </Select>
        )}
      </div>

      {sessionActive && (
        <Badge tone="success" className="mb-2" title="De agent onthoudt eerdere berichten in dit gesprek">
          <Link2 className="h-3 w-3" />
          Gesprek actief
        </Badge>
      )}
    </div>
  );
}
